"use client";

import React, { ReactNode, useState, useEffect, useTransition } from "react";
import {
  getProductChecksums,
  ProductChecksum,
} from "@/helpers/api/WIA/product";
import { Spinner } from "@/icons";
import { isEditing } from "@builder.io/sdk-react";

export type ExternalSourceType = "Product Checksums";

export interface ExternalSourceConfig<T = unknown> {
  fetchData: () => Promise<T[] | null | undefined>;
  renderRow: (item: T, index: number) => ReactNode;
  loadingColSpan: number;
}

export const EXTERNAL_SOURCES: Record<
  ExternalSourceType,
  ExternalSourceConfig
> = {
  "Product Checksums": {
    fetchData: getProductChecksums,
    renderRow: (data: unknown, index: number) => {
      const checksum = data as ProductChecksum;
      return (
        <tr key={index} className="text-body-s text-body-primary">
          <td>{checksum.productShortName}</td>
          <td>{checksum.fileName}</td>
          <td>
            {new Date(checksum.releaseDate).toLocaleDateString("en-US", {
              year: "numeric",
              month: "numeric",
              day: "numeric",
            })}
          </td>
          <td>{checksum.platformName}</td>
          <td className="break-all">{checksum.checkSumSha256}</td>
          <td className="break-all">{checksum.checkSumSha1}</td>
          <td className="break-all">{checksum.checkSumMd5}</td>
        </tr>
      );
    },
    loadingColSpan: 7,
  },
};

interface TableExternalDataRowsProps {
  externalSource?: ExternalSourceType;
}

export const TableExternalDataRows: React.FC<TableExternalDataRowsProps> = ({
  externalSource,
}) => {
  const [externalData, setExternalData] = useState<unknown[]>([]);
  const [loading, startTransition] = useTransition();

  useEffect(() => {
    if (!isEditing() && externalSource) {
      const config = EXTERNAL_SOURCES[externalSource];
      if (config) {
        startTransition(async () => {
          const data = await config.fetchData();
          if (data) {
            startTransition(() => {
              setExternalData(data);
            });
          }
        });
      }
    }
  }, [externalSource]);

  if (!externalSource) return null;

  const config = EXTERNAL_SOURCES[externalSource];
  if (!config) return null;

  if (loading || isEditing()) {
    return (
      <tr>
        <td colSpan={config.loadingColSpan}>
          <div className="mx-auto w-fit animate-spin">
            <Spinner />
          </div>
        </td>
      </tr>
    );
  }

  return (
    <>{externalData?.map((item, index) => config.renderRow(item, index))}</>
  );
};
